import { renderButtonContent } from 'components/Button/renderButtonContent';
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import './Button.css';
import type {
  IconGlyph,
  IconStyle,
} from '@ipfabric/ui-components/Icon/IconGlyph';
import { getButtonClassNames } from './getButtonClassNames';

export type ButtonSize = 'xxxs' | 'xxs' | 'xs' | 'sm' | 'base' | 'xl';
export type ButtonAccent = 'positive' | 'negative';
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type IconPosition = 'left' | 'right';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Not supported by tertiary variant
   */
  accent?: ButtonAccent;
  disabled?: boolean;
  className?: string;
  /**
   * Size should be used only with primary variant
   */
  size?: ButtonSize;
  variant: ButtonVariant;
  icon?: IconGlyph;
  iconStyle?: IconStyle;
  iconPosition?: IconPosition;
  /**
   * Inverse color for dark background. Currently supported only by tertiary
   */
  inversed?: boolean;
  loading?: boolean;
  // Typed as string in HTMLButtonElement, let's make it more strict
  type: 'button' | 'reset' | 'submit';
  dataTestId?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      accent,
      disabled,
      className,
      size,
      variant,
      onClick,
      children,
      icon,
      inversed,
      loading,
      iconStyle,
      iconPosition,
      dataTestId,
      ...restProps
    },
    ref,
  ) => (
    <button
      className={getButtonClassNames(
        children,
        variant,
        className,
        accent,
        size,
        inversed,
        iconPosition,
      )}
      disabled={loading || disabled}
      ref={ref}
      onClick={onClick}
      data-testid={dataTestId}
      {...restProps}
    >
      {renderButtonContent({
        children,
        icon,
        iconStyle,
        iconPosition,
        loading,
        size,
      })}
    </button>
  ),
);

Button.displayName = 'Button';
